import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import './ImportCarePlan.css';
import { appName } from '../brand';

/**
 * Support-plan PDF import wizard.
 *
 * 4 steps: Individual Info → Upload → Processing → Results.
 *
 * The processing step is intentionally tolerant of slow / hung backend
 * calls (extraction on a 30-page support plan can take minutes). Two
 * things make that experience pleasant for the demo:
 *
 * 1. A "synthetic" progress engine animates a rotating set of status
 *    messages and ticks the progress bar forward on a timer, regardless
 *    of what the backend reports. As soon as the backend says completed,
 *    we snap to 100%.
 *
 * 2. The user can navigate away at any time — the backend job lives in
 *    a FastAPI BackgroundTasks worker and finishes on its own. The
 *    component clears its polling timers on unmount and guards setState
 *    calls behind a mountedRef so a late response never crashes.
 */

// Generic progress copy — intentionally free of health/PII language.
const SYNTHETIC_STAGES = [
  { from: 8,  to: 18, message: '📄 Reading the document…' },
  { from: 18, to: 30, message: '🔍 Identifying document sections…' },
  { from: 30, to: 45, message: '✍️ Extracting content…' },
  { from: 45, to: 60, message: '🗂️ Organizing the information…' },
  { from: 60, to: 72, message: '🧩 Structuring the record…' },
  { from: 72, to: 82, message: '🔧 Formatting the extracted content…' },
  { from: 82, to: 90, message: '✅ Finalizing…' },
  { from: 90, to: 95, message: '💾 Saving to the record…' },
];

const ImportCarePlan = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    patientName: '',
    patientAge: '',
    careAgent: 'AI Import System',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [processId, setProcessId] = useState(null);
  const [processStatus, setProcessStatus] = useState(null);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Synthetic progress driven on the client so the user always sees motion,
  // even if the OpenRouter call is slow. Caps at 95% until the backend confirms.
  const [syntheticProgress, setSyntheticProgress] = useState(0);
  const [syntheticMessage, setSyntheticMessage] = useState(SYNTHETIC_STAGES[0].message);

  // Guards against late polling responses setting state after unmount.
  const mountedRef = useRef(true);
  const pollingRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (animationRef.current) clearInterval(animationRef.current);
    };
  }, []);

  // Backend status polling — runs only while we're on step 3 with a process_id.
  useEffect(() => {
    if (!processId || step !== 3) return undefined;

    const tick = async () => {
      try {
        const status = await apiService.getImportStatus(processId);
        if (!mountedRef.current) return;
        setProcessStatus(status);
        if (status.status === 'completed' || status.status === 'error') {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setSyntheticProgress(100);
          setSyntheticMessage(
            status.status === 'completed'
              ? '✅ Import complete!'
              : '❌ Something went wrong — see details below.',
          );
          // Hold the completed view briefly so the user sees 100%, then advance.
          setTimeout(() => mountedRef.current && setStep(4), 800);
        }
      } catch (err) {
        // Silently retry on the next tick — don't crash the UI.
        // eslint-disable-next-line no-console
        console.debug('Status poll error (will retry):', err.message);
      }
    };

    tick(); // fire immediately so we don't wait 2s for the first update
    pollingRef.current = setInterval(tick, 2000);
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [processId, step]);

  // Synthetic progress engine — independent of the backend so the UI
  // always feels alive. Walks through SYNTHETIC_STAGES, capping at 95%
  // until the real backend status hits completed.
  useEffect(() => {
    if (step !== 3) return undefined;

    let stageIdx = 0;
    let progress = 5;
    setSyntheticProgress(progress);
    setSyntheticMessage(SYNTHETIC_STAGES[0].message);

    animationRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      // If backend has terminated, the polling effect will jump us to 100.
      if (processStatus?.status === 'completed' || processStatus?.status === 'error') {
        return;
      }
      const stage = SYNTHETIC_STAGES[stageIdx];
      // Ease toward the stage's "to" value
      progress = Math.min(stage.to, progress + 0.6 + Math.random() * 0.8);
      setSyntheticProgress(Math.round(progress));
      setSyntheticMessage(stage.message);
      if (progress >= stage.to - 0.5 && stageIdx < SYNTHETIC_STAGES.length - 1) {
        stageIdx += 1;
        progress = SYNTHETIC_STAGES[stageIdx].from;
      }
    }, 700);

    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Please select a PDF. Word documents need to be converted via File → Save As → PDF first.');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError('File size must be less than 50MB.');
      return;
    }
    setSelectedFile(file);
    setError(null);
  };

  const handleNext = () => {
    if (step === 1) {
      if (!formData.patientName.trim()) {
        setError('Individual name is required.');
        return;
      }
      setError(null);
      setStep(2);
    } else if (step === 2) {
      if (!selectedFile) {
        setError('Please select a file to upload.');
        return;
      }
      handleUpload();
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !formData.patientName) return;
    setIsUploading(true);
    setError(null);
    try {
      // Use the OpenRouter single-shot PDF path (lower-cost, native PDF support)
      const result = await apiService.uploadDocumentSimple(formData.patientName, selectedFile);
      if (!mountedRef.current) return;
      setProcessId(result.process_id);
      setStep(3);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(`Upload failed: ${err.message}`);
    } finally {
      if (mountedRef.current) setIsUploading(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setFormData({ patientName: '', patientAge: '', careAgent: 'AI Import System' });
    setSelectedFile(null);
    setProcessId(null);
    setProcessStatus(null);
    setError(null);
    setSyntheticProgress(0);
  };

  // Backend may be at 35% for a while; show the higher of synthetic vs real.
  const visibleProgress = Math.max(
    syntheticProgress,
    Math.min(95, processStatus?.progress || 0),
  );

  // ---------------- STEP RENDERERS ----------------

  const renderStepIndicator = () => (
    <div className="step-indicator">
      <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
        <div className="step-number">1</div>
        <div className="step-label">Individual Info</div>
      </div>
      <div className={`step-connector ${step > 1 ? 'completed' : ''}`}></div>
      <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
        <div className="step-number">2</div>
        <div className="step-label">Upload Document</div>
      </div>
      <div className={`step-connector ${step > 2 ? 'completed' : ''}`}></div>
      <div className={`step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}>
        <div className="step-number">3</div>
        <div className="step-label">AI Processing</div>
      </div>
      <div className={`step-connector ${step > 3 ? 'completed' : ''}`}></div>
      <div className={`step ${step >= 4 ? 'active' : ''}`}>
        <div className="step-number">4</div>
        <div className="step-label">Results</div>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="import-step">
      <h2>👤 Individual Information</h2>
      <p className="step-description">
        Enter the individual's basic information. This will be used to create their profile and process their support plan document.
      </p>
      <div className="form-group">
        <label htmlFor="patientName">Individual Name <span className="required">*</span></label>
        <input
          type="text"
          id="patientName"
          name="patientName"
          value={formData.patientName}
          onChange={handleInputChange}
          placeholder="Enter the individual's full name"
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor="patientAge">Age</label>
        <input
          type="number"
          id="patientAge"
          name="patientAge"
          value={formData.patientAge}
          onChange={handleInputChange}
          placeholder="Optional"
          min="0"
          max="120"
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="import-step">
      <h2>📄 Upload Support Plan Document</h2>
      <p className="step-description">
        Select the PDF containing the individual's support plan. Our AI will read it and populate the profile automatically.
      </p>
      <div className="conversion-notice">
        <strong>📋 PDF only.</strong> If you have a Word document, save it as a PDF first (File → Save As → PDF).
      </div>

      <div className="file-upload-area">
        <input
          type="file"
          id="fileInput"
          accept=".pdf"
          onChange={handleFileSelect}
          className="file-input"
        />
        <label htmlFor="fileInput" className="file-upload-label">
          <div className="upload-icon">📁</div>
          <div className="upload-text">
            {selectedFile ? (
              <>
                <strong>{selectedFile.name}</strong>
                <br />
                <small>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</small>
              </>
            ) : (
              <>
                <strong>Click to select a file</strong>
                <br />
                <small>PDF up to 50MB</small>
              </>
            )}
          </div>
        </label>
      </div>

      {selectedFile && (
        <div className="file-info">
          <div className="file-details">
            <h4>📋 File Details</h4>
            <p><strong>Name:</strong> {selectedFile.name}</p>
            <p><strong>Size:</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
            <p><strong>Type:</strong> {selectedFile.type || 'PDF Document'}</p>
            <p><strong>Last Modified:</strong> {new Date(selectedFile.lastModified).toLocaleDateString()}</p>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="import-step">
      <h2>⚙️ Processing Your Document</h2>
      <p className="step-description">
        Our AI is analyzing the document and populating the individual's support plan. This usually takes 1-3 minutes for a typical document.
      </p>

      <div className="processing-container">
        <div className="progress-circle">
          <div className="progress-status-icon">🧠</div>
          <div className="progress-percentage">{visibleProgress}%</div>
        </div>

        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${visibleProgress}%` }}></div>
        </div>

        <div className="status-details">
          <h4>{syntheticMessage}</h4>
          <small>Last update: {new Date().toLocaleTimeString('en-CA', { timeZone: 'America/Regina' })}</small>
        </div>

        <div className="nav-safe-banner">
          <span className="banner-icon">💡</span>
          <span>
            Processing continues in the background. You can <strong>navigate away</strong> and check back later —
            the import won't be interrupted.
          </span>
        </div>

        <div className="processing-actions">
          <button
            className="primary-btn"
            onClick={() => navigate('/individuals')}
          >
            Skip ahead → view individuals
          </button>
          <button
            className="secondary-btn"
            onClick={() => navigate('/')}
          >
            Back to dashboard
          </button>
        </div>

        <div className="processing-info">
          <h4>🔍 What's happening behind the scenes?</h4>
          <ul>
            <li>📤 Document uploaded and validated</li>
            <li>🧠 AI is reading and understanding the content</li>
            <li>🗂️ Extracting information into structured sections</li>
            <li>💾 Saving data to the individual's support plan</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="import-step">
      {processStatus?.status === 'completed' ? (
        <>
          <h2>✅ Import Completed Successfully!</h2>
          <p className="step-description">
            The support plan document has been processed and the individual's profile is ready to view.
          </p>

          <div className="results-container">
            <div className="success-stats">
              <div className="stat-card">
                <div className="stat-number">{processStatus.sections_processed || '—'}</div>
                <div className="stat-label">Sections Processed</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{processStatus.sections_failed || 0}</div>
                <div className="stat-label">Errors</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{processStatus.patient_id || '—'}</div>
                <div className="stat-label">Individual ID</div>
              </div>
            </div>

            <div className="next-steps">
              <h4>🎉 What's Next?</h4>
              <p>The individual's support plan is now available. You can:</p>
              <div className="action-buttons">
                {processStatus.patient_id && (
                  <button
                    className="primary-btn"
                    onClick={() => navigate(`/individuals/${processStatus.patient_id}/care-plan`)}
                  >
                    📋 View Support Plan
                  </button>
                )}
                <button
                  className="secondary-btn"
                  onClick={() => navigate('/individuals')}
                >
                  👥 Back to Individual List
                </button>
                <button className="secondary-btn" onClick={handleReset}>
                  📁 Import Another Document
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <h2>❌ Import Failed</h2>
          <p className="step-description">
            Unfortunately there was an error processing the document.
          </p>
          <div className="error-container">
            <div className="error-details">
              <h4>Error Details:</h4>
              <p>{processStatus?.details || 'Unknown error occurred.'}</p>
              <small>
                Time: {processStatus?.timestamp ? new Date(processStatus.timestamp).toLocaleString('en-CA', { timeZone: 'America/Regina' }) : 'Unknown'}
              </small>
            </div>
            <div className="retry-options">
              <h4>🔄 What you can do:</h4>
              <ul>
                <li>Check that the document is a valid PDF</li>
                <li>Ensure the PDF contains support plan information</li>
                <li>Try with a smaller file if the document is very large</li>
              </ul>
              <button className="primary-btn" onClick={handleReset}>🔄 Try Again</button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="import-care-plan">
      <header className="app-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo">{appName()}</div>
          </div>
          <nav className="nav-menu">
            <button className="nav-item" onClick={() => navigate('/')}>Dashboard</button>
            <button className="nav-item" onClick={() => navigate('/individuals')}>Support Plans</button>
            <button className="nav-item active">Import</button>
            <button
              className="nav-item"
              onClick={async () => {
                try { await apiService.logout(); } catch (_) {}
                window.location.reload();
              }}
              title="Sign out"
            >
              Sign out
            </button>
          </nav>
        </div>
      </header>

      <div className="import-container">
        <div className="import-header">
          <h1>🤖 AI-Powered Support Plan Import</h1>
          <p>Upload an existing support plan document and let our AI extract and organize the information.</p>
        </div>

        {renderStepIndicator()}

        <div className="import-content">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}

          {error && (
            <div className="error-message">
              <div className="error-icon">⚠️</div>
              <div className="error-text">{error}</div>
            </div>
          )}

          {step < 3 && (
            <div className="step-actions">
              {step > 1 && (
                <button
                  className="secondary-btn"
                  onClick={() => setStep(step - 1)}
                  disabled={isUploading}
                >
                  ← Back
                </button>
              )}
              <button
                className="primary-btn"
                onClick={handleNext}
                disabled={isUploading}
              >
                {step === 2 ? (isUploading ? 'Uploading…' : 'Upload & Process') : 'Next →'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportCarePlan;
